import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import {
    fetchAIPurchaseOverall,
    fetchAIPurchaseByState,
    useAIPurchaseOverall,
    useAIPurchaseStates,
    useAIPurchaseLoading,
    useAIPurchaseError,
} from '../../store/slices/aiPurchaseAnalyticsSlice';
import { useAIPurchaseDateRangeParams } from './hooks/useAIPurchaseDateRangeParams';
import AIPurchaseDateRangeControls from './components/AIPurchaseDateRangeControls';
import OverallSummaryCard from './components/OverallSummaryCard';
import StateBreakdownGrid from './components/StateBreakdownGrid';

/**
 * Page 1 — AI Purchase Numbers overview: overall adoption summary + per-state
 * breakdown (all 5 AI-enabled states), each state card navigating to its own
 * brand-breakdown page. Reached via the homepage's "AI Purchase Numbers" card.
 */
export default function AIPurchaseNumbersOverview() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { fromDate, toDate, queryString, setDateRange } = useAIPurchaseDateRangeParams();

    const overall = useAIPurchaseOverall();
    const states = useAIPurchaseStates();
    const loading = useAIPurchaseLoading();
    const error = useAIPurchaseError();

    useEffect(() => {
        dispatch(fetchAIPurchaseOverall({ fromDate, toDate }));
        dispatch(fetchAIPurchaseByState({ fromDate, toDate }));
    }, [dispatch, fromDate, toDate]);

    const handleSelectState = (state: string) => {
        navigate(`/ai-purchase-numbers/state/${encodeURIComponent(state)}?${queryString}`);
    };

    return (
        <div className="w-full space-y-6">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-4xl font-bold text-[#1e293b] leading-tight">
                        AI Purchase Adoption
                    </h2>
                    <p className="text-lg text-[#64748b] mt-2">
                        Track adoption of the AI purchase flow across Uttar Pradesh, Assam,
                        Maharashtra, Odisha and Rajasthan.
                    </p>
                </div>

                <AIPurchaseDateRangeControls
                    fromDate={fromDate}
                    toDate={toDate}
                    onApply={setDateRange}
                />
            </div>

            <OverallSummaryCard
                title="Overall AI Purchase Adoption"
                description="Across all AI-enabled states for the selected date range."
                overall={overall}
                loading={loading.overall === 'loading'}
                error={error.overall}
            />

            <div>
                <h3 className="text-lg font-semibold text-[#1e293b] mb-3">By state</h3>
                <StateBreakdownGrid
                    states={states}
                    loading={loading.states === 'loading'}
                    error={error.states}
                    onSelectState={handleSelectState}
                />
            </div>
        </div>
    );
}
